import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { useSeo } from "@/hooks/useSeo";

const SUBMIT_URL = "https://readdy.ai/api/form/daf8po3ep053ijub1lbg";

const contactInfo = [
  {
    icon: "ri-mail-send-line",
    title: "投稿瓜线索",
    desc: "有值得盘点的热点线索？提交后我们会评估公共价值与可信度。",
  },
  {
    icon: "ri-question-line",
    title: "内容建议",
    desc: "对我们的榜单、复盘与栏目有想法？欢迎告诉我们改进方向。",
  },
  {
    icon: "ri-briefcase-4-line",
    title: "媒体 / 合作",
    desc: "内容授权、栏目共建或品牌合作，都可以通过表单与我们取得联系。",
  },
];

type Status = "idle" | "success" | "error";

export default function Contact() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  useSeo({
    title: "联系我们 - 吃瓜榜单投稿与反馈",
    description:
      "联系吃瓜榜单：投稿瓜线索、提出内容建议或洽谈媒体合作，填写联系我们表单即可，我们会在工作日尽快回复。",
    keywords: "联系我们,投稿,瓜线索,吃瓜榜单,内容反馈",
    canonicalPath: "/contact",
    ogType: "website",
    jsonLdId: "contact",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "联系我们 - 吃瓜榜单",
      description: "吃瓜榜单投稿、反馈与合作联系页",
      url: "https://chiguabangdan.example/contact",
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://chiguabangdan.example/" },
          { "@type": "ListItem", position: 2, name: "联系我们", item: "https://chiguabangdan.example/contact" },
        ],
      },
    },
  });

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const honeypot = String(data.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("感谢你的留言，我们会尽快查看并与你联系。");
      return;
    }
    if (honeypot) data.delete("website_alt");

    try {
      const res = await fetch(SUBMIT_URL, { method: "POST", body: data });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || responseText || "";
      if (res.ok && parsed?.code === "OK" && !serverMsg.toLowerCase().includes("spam")) {
        setStatus("success");
        setMessage("投稿已收到，感谢你的支持，我们会尽快跟进并回复你。");
        form.reset();
        return;
      }
      setStatus("error");
      setMessage(
        serverMsg.includes("spam") || serverMsg.includes("表单")
          ? serverMsg
          : "提交未能成功，请稍后再试或检查填写内容。"
      );
    } catch {
      setStatus("error");
      setMessage("网络似乎开小差了，请稍后重试。");
    }
  }

  const inputCls =
    "w-full rounded-md border-0 bg-background-100 px-4 py-3 text-sm text-foreground-950 ring-1 ring-background-200 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400";

  return (
    <Layout>
      <div className="mx-auto w-full max-w-7xl px-4 pt-24 md:px-6">
        <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-foreground-500">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <span className="text-foreground-800">联系我们</span>
        </nav>

        <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-primary-600">Contact</p>
            <h1 className="text-3xl font-black text-foreground-950 md:text-4xl">联系我们，一起把瓜讲得更清楚</h1>
            <p className="mt-4 max-w-lg text-sm leading-7 text-foreground-700">
              无论是投稿你发现的瓜线索、对我们的内容提出建议，还是有媒体与商务合作的想法，都欢迎填写右侧表单。
              我们会尽量在 3 个工作日内回复。
            </p>

            <div className="mt-8 flex flex-col gap-4">
              {contactInfo.map((c) => (
                <div key={c.title} className="flex gap-4 rounded-lg bg-background-100/70 p-5 ring-1 ring-background-200/60">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                    <i className={`${c.icon} text-xl`} aria-hidden="true"></i>
                  </span>
                  <div>
                    <h2 className="text-sm font-bold text-foreground-950">{c.title}</h2>
                    <p className="mt-1 text-sm leading-6 text-foreground-600">{c.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <p className="mt-6 text-xs leading-5 text-foreground-500">
              我们承诺：你提交的联系信息仅用于本次回复与内容沟通，不会用于其他用途。
            </p>
          </div>

          <div className="rounded-lg bg-background-100/70 p-6 ring-1 ring-background-200/70 md:p-8">
            <h2 className="text-lg font-black text-foreground-950">在线留言</h2>
            <p className="mt-1 text-sm text-foreground-600">带 * 为必填项</p>

            {status === "success" && (
              <div className="mt-4 flex items-start gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-900">
                <i className="ri-checkbox-circle-line mt-0.5" aria-hidden="true"></i>
                <span>{message}</span>
              </div>
            )}
            {status === "error" && (
              <div className="mt-4 flex items-start gap-2 rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-900">
                <i className="ri-error-warning-line mt-0.5" aria-hidden="true"></i>
                <span>{message}</span>
              </div>
            )}

            <form id="contact-form" data-readdy-form="吃瓜榜单联系投稿" onSubmit={handleSubmit} className="mt-5 flex flex-col gap-4">
              <div>
                <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  你的称呼 <span className="text-primary-600">*</span>
                </label>
                <input id="name" name="name" type="text" required maxLength={30} placeholder="怎么称呼你" className={inputCls} />
              </div>

              <div>
                <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  联系邮箱 <span className="text-primary-600">*</span>
                </label>
                <input id="email" name="email" type="email" required placeholder="you@example.com" className={inputCls} />
              </div>

              <div>
                <label htmlFor="topic" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  留言类型 <span className="text-primary-600">*</span>
                </label>
                <select id="topic" name="topic" required className={`${inputCls} appearance-none`} defaultValue="">
                  <option value="" disabled>请选择类型</option>
                  <option value="投稿瓜线索">投稿瓜线索</option>
                  <option value="内容建议">内容建议</option>
                  <option value="媒体 / 合作">媒体 / 合作</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  留言内容 <span className="text-primary-600">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请简要描述你要反馈的内容、事件或合作想法…"
                  className={`${inputCls} resize-none`}
                ></textarea>
                <p className="mt-1 text-right text-xs text-foreground-400">最多 500 字</p>
              </div>

              {/* anti-spam */}
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                className="whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                提交留言
                <i className="ri-send-plane-fill ml-1" aria-hidden="true"></i>
              </button>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
}